import type { Action, Module, Scope } from '@dtable/rbac';
import {
  ApartmentOutlined,
  AuditOutlined,
  BankOutlined,
  BarChartOutlined,
  BellOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  CreditCardOutlined,
  CustomerServiceOutlined,
  DashboardOutlined,
  LaptopOutlined,
  FileSearchOutlined,
  FileTextOutlined,
  InboxOutlined,
  DeploymentUnitOutlined,
  LineChartOutlined,
  ProjectOutlined,
  SettingOutlined,
  SolutionOutlined,
  TeamOutlined,
  UserAddOutlined,
  UserOutlined,
} from '@ant-design/icons';
import type { ComponentType } from 'react';

export interface NavRequirement {
  module: Module;
  action: Action;
  scope: Scope;
}

export type NavGroup = 'core' | 'my-work' | 'people-org' | 'admin';

export interface NavItem {
  key: string;
  label: string;
  path: string;
  icon: ComponentType;
  group: NavGroup;
  requires: NavRequirement[];
}

export const NAV_GROUP_LABELS: Record<NavGroup, string> = {
  'core': '',
  'my-work': 'My Work',
  'people-org': 'People & Org',
  'admin': 'Admin',
};

export const NAV_ITEMS: NavItem[] = [
  // ---- Core (always visible after login) ----
  {
    key: 'dashboard',
    label: 'Home',
    path: '/',
    icon: DashboardOutlined,
    group: 'core',
    requires: [],
  },
  {
    key: 'inbox',
    label: 'Inbox',
    path: '/inbox',
    icon: InboxOutlined,
    group: 'core',
    requires: [],
  },
  {
    key: 'me',
    label: 'Me',
    path: '/me',
    icon: UserOutlined,
    group: 'core',
    requires: [],
  },

  // ---- My Work (self-service daily ops) ----
  {
    key: 'attendance',
    label: 'Attendance',
    path: '/attendance',
    icon: ClockCircleOutlined,
    group: 'my-work',
    requires: [{ module: 'attendance', action: 'view', scope: 'self' }],
  },
  {
    key: 'leave',
    label: 'Leave',
    path: '/leave',
    icon: CalendarOutlined,
    group: 'my-work',
    requires: [{ module: 'leave', action: 'view', scope: 'self' }],
  },
  {
    key: 'payroll',
    label: 'Payroll',
    path: '/payroll',
    icon: CreditCardOutlined,
    group: 'my-work',
    requires: [{ module: 'payroll', action: 'view', scope: 'self' }],
  },
  {
    key: 'expenses',
    label: 'Expenses',
    path: '/expenses',
    icon: BankOutlined,
    group: 'my-work',
    requires: [{ module: 'expenses', action: 'view', scope: 'self' }],
  },
  {
    key: 'performance',
    label: 'Performance',
    path: '/performance',
    icon: LineChartOutlined,
    group: 'my-work',
    requires: [{ module: 'performance', action: 'view', scope: 'self' }],
  },
  {
    key: 'documents',
    label: 'Documents',
    path: '/documents',
    icon: FileTextOutlined,
    group: 'my-work',
    requires: [{ module: 'documents', action: 'view', scope: 'self' }],
  },
  {
    key: 'engage',
    label: 'Engage',
    path: '/engage',
    icon: BellOutlined,
    group: 'my-work',
    requires: [{ module: 'engage', action: 'view', scope: 'self' }],
  },
  {
    key: 'helpdesk',
    label: 'Helpdesk',
    path: '/helpdesk',
    icon: CustomerServiceOutlined,
    group: 'my-work',
    requires: [{ module: 'helpdesk', action: 'view', scope: 'self' }],
  },

  // ---- People & Org (team / management) ----
  {
    key: 'employees',
    label: 'Employees',
    path: '/employees',
    icon: TeamOutlined,
    group: 'people-org',
    requires: [{ module: 'employees', action: 'view', scope: 'team' }],
  },
  {
    key: 'org',
    label: 'Org Structure',
    path: '/org',
    icon: ApartmentOutlined,
    group: 'people-org',
    requires: [{ module: 'org-structure', action: 'view', scope: 'org' }],
  },
  {
    key: 'onboarding',
    label: 'Onboarding',
    path: '/onboarding',
    icon: UserAddOutlined,
    group: 'people-org',
    requires: [{ module: 'onboarding', action: 'view', scope: 'team' }],
  },
  {
    key: 'exits',
    label: 'Exits',
    path: '/exits',
    icon: SolutionOutlined,
    group: 'people-org',
    requires: [
      { module: 'exits', action: 'view', scope: 'team' },
      { module: 'exits', action: 'approve', scope: 'team' },
      { module: 'exits', action: 'edit', scope: 'org' },
    ],
  },
  {
    key: 'assets',
    label: 'Assets',
    path: '/assets',
    icon: LaptopOutlined,
    group: 'people-org',
    requires: [{ module: 'assets', action: 'view', scope: 'org' }],
  },
  {
    key: 'hiring',
    label: 'Hiring',
    path: '/hiring',
    icon: FileSearchOutlined,
    group: 'people-org',
    requires: [{ module: 'hiring', action: 'view', scope: 'team' }],
  },
  // Projects nav removed — Operations covers day-to-day project/task work.
  // Timesheet + Utilization pages still live under /projects/:tab if you
  // want to re-surface them later. Re-add this block to restore the menu:
  //   { key: 'projects', label: 'Projects', path: '/projects',
  //     icon: ProjectOutlined, group: 'people-org',
  //     requires: [{ module: 'projects', action: 'view', scope: 'self' }] }
  {
    key: 'operations',
    label: 'Operations',
    path: '/operations',
    icon: DeploymentUnitOutlined,
    group: 'people-org',
    requires: [{ module: 'operations', action: 'view', scope: 'self' }],
  },
  {
    key: 'planning',
    label: 'Planning',
    path: '/planning',
    icon: LineChartOutlined,
    group: 'people-org',
    requires: [{ module: 'planning', action: 'view', scope: 'org' }],
  },

  // ---- Admin ----
  {
    key: 'reports',
    label: 'Reports',
    path: '/reports',
    icon: BarChartOutlined,
    group: 'admin',
    requires: [
      { module: 'reports', action: 'view', scope: 'org' },
      { module: 'reports:payroll', action: 'view', scope: 'org' },
      { module: 'reports:hiring', action: 'view', scope: 'org' },
      { module: 'reports:assets', action: 'view', scope: 'org' },
      { module: 'reports:team', action: 'view', scope: 'team' },
    ],
  },
  {
    key: 'audit-logs',
    label: 'Audit Logs',
    path: '/audit-logs',
    icon: AuditOutlined,
    group: 'admin',
    requires: [{ module: 'audit-logs', action: 'view', scope: 'org' }],
  },
  {
    key: 'settings',
    label: 'Settings',
    path: '/settings',
    icon: SettingOutlined,
    group: 'admin',
    requires: [{ module: 'settings', action: 'view', scope: 'org' }],
  },
];
